
import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const statusCardVariants = cva(
  'group relative overflow-hidden rounded-xl border p-6 transition-all duration-300 hover:shadow-subtle',
  {
    variants: {
      variant: {
        default: 'bg-card border-border',
        primary: 'bg-primary/5 border-primary/20',
        warning: 'bg-yellow-500/5 border-yellow-500/20',
        success: 'bg-green-500/5 border-green-500/20',
        danger: 'bg-red-500/5 border-red-500/20',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

interface StatusCardProps extends VariantProps<typeof statusCardVariants> {
  icon: LucideIcon;
  title: string;
  value: string | number;
  description?: string;
  className?: string;
  iconClassName?: string;
  index?: number;
}

export const StatusCard: React.FC<StatusCardProps> = ({
  icon: Icon,
  title,
  value,
  description,
  variant,
  className,
  iconClassName,
  index = 0,
}) => {
  return (
    <div
      className={cn(
        statusCardVariants({ variant }),
        'opacity-0 animate-fade-up',
        className
      )}
      style={{ animationDelay: `${(index + 1) * 100}ms` }}
    >
      <div className="flex items-start justify-between mb-2">
        <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
        <div
          className={cn(
            'flex items-center justify-center w-8 h-8 rounded-lg',
            {
              'bg-primary/10 text-primary': variant === 'primary',
              'bg-yellow-500/10 text-yellow-500': variant === 'warning',
              'bg-green-500/10 text-green-500': variant === 'success',
              'bg-red-500/10 text-red-500': variant === 'danger',
              'bg-muted text-muted-foreground': variant === 'default',
            },
            iconClassName
          )}
        >
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="mt-2">
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </div>
    </div>
  );
};

export default StatusCard;
